import type { Addon, Item } from "../api/menu";
import { lunchKeyOfItem, lunchPairKey, type LunchCounters } from "./lunchCatalog";

// Порциите в кошницата по брояч (lib/lunchCatalog.ts `LunchCounters`): всеки
// ред от дневно меню + добавките му. Добавка тип 1 е с абсолютно количество,
// тип 2/3 — по 1 на продукт (× бройката) — както `buildBillElements`.

export interface PortionCartLine {
  itemId: number;
  quantity: number;
  addons: Record<number, Record<number, number>>;
}

export const lunchUsageByKey = (
  lines: PortionCartLine[],
  itemsById: Map<number, Item>,
  addonsById: Map<number, Addon>,
  counters: LunchCounters | undefined,
): Map<string, number> => {
  const usage = new Map<string, number>();
  if (!counters) return usage;
  const add = (key: string | undefined, quantity: number) => {
    if (key) usage.set(key, (usage.get(key) ?? 0) + quantity);
  };
  for (const line of lines) {
    const item = itemsById.get(line.itemId);
    if (item?.lunchMenuId == null) continue;
    add(lunchKeyOfItem(item), line.quantity);
    for (const [addonId, quantityByOption] of Object.entries(line.addons)) {
      const absolute = addonsById.get(Number(addonId))?.type === 1;
      for (const [optionId, quantity] of Object.entries(quantityByOption)) {
        if (quantity <= 0) continue;
        add(lunchPairKey(item.lunchMenuId, Number(optionId)), absolute ? quantity : quantity * line.quantity);
      }
    }
  }
  return usage;
};

/** Колко още могат да се добавят от брояча; Infinity — без ограничение. */
export const lunchRoomOfKey = (
  counters: LunchCounters | undefined,
  usage: Map<string, number>,
  key: string | undefined,
): number => {
  if (!counters || !key || !(key in counters.leftByKey)) return Infinity;
  return counters.leftByKey[key] - (usage.get(key) ?? 0);
};

/**
 * Най-голямата бройка на ред от дневно меню с тези добавки при кошница с
 * `usage`: основното — до остатъка на брояча му; всяка избрана добавка тип 2/3
 * (по N на продукт) — до остатъка ѝ / N; тип 1 (абсолютна) не расте с
 * бройката, но не стига ли — 0. Infinity — без ограничение.
 */
export const lunchMaxLineQuantity = (
  item: Item | null | undefined,
  addons: Record<number, Record<number, number>>,
  addonsById: Map<number, Addon>,
  counters: LunchCounters | undefined,
  usage: Map<string, number>,
): number => {
  if (!item || item.lunchMenuId == null || !counters) return Infinity;
  let max = lunchRoomOfKey(counters, usage, lunchKeyOfItem(item));
  for (const [addonId, quantityByOption] of Object.entries(addons)) {
    const absolute = addonsById.get(Number(addonId))?.type === 1;
    for (const [optionId, quantity] of Object.entries(quantityByOption)) {
      if (quantity <= 0) continue;
      const room = lunchRoomOfKey(counters, usage, lunchPairKey(item.lunchMenuId, Number(optionId)));
      if (!Number.isFinite(room)) continue;
      max = absolute ? (quantity > room ? 0 : max) : Math.min(max, Math.floor(room / quantity));
    }
  }
  return Math.max(0, max);
};
